/**
 * Gerador do organograma automático (cargos + departamentos + gestor direto).
 *
 * Função PURA: sem React e sem imports — recebe os dados do endpoint
 * `GET /api/organogramas/dados-geracao` e devolve a árvore (nós + arestas, sem posição)
 * e os avisos da revisão. As posições/cores/formato do React Flow ficam em
 * `organogramaGeradorCanvas.ts`. Regras: docs/organograma-automatico-cargos-2026-10-08.md (seções 6 a 8) e,
 * para o card de quadro (cargo sem ninguém cadastrado), docs/importacao-cargos-quadro-2026-10-08.md (seção 5).
 */

export interface DadosGeracaoEmpresa {
  id: string;
  nome: string;
}

export interface DadosGeracaoDepartamento {
  id: string;
  nome: string;
}

export interface DadosGeracaoCargo {
  id: string;
  nome: string;
  /** Quantidade informada no quadro; com ninguém cadastrado no cargo, vira um card de quadro. */
  quantidade_quadro?: number | null;
  /** Departamento onde o card de quadro aparece (sem ele, vai para "Sem departamento"). Também é o departamento
   *  de quem ocupa o cargo e não tem departamento próprio no vínculo. */
  departamento_id?: string | null;
}

export interface DadosGeracaoPessoa {
  usuario_id: string;
  nome: string;
  email?: string | null;
  departamento_id: string | null;
  cargo_id: string | null;
  gestor_usuario_id: string | null;
}

export interface DadosGeracao {
  empresa: DadosGeracaoEmpresa;
  departamentos: DadosGeracaoDepartamento[];
  cargos: DadosGeracaoCargo[];
  pessoas: DadosGeracaoPessoa[];
}

export type TipoNoGerado = 'empresa' | 'departamento' | 'sem_departamento' | 'pessoa' | 'cargo_quadro';

export interface NoGerado {
  /** Chave estável: `empresa`, `dept:<id>`, `sem-departamento`, `pessoa:<usuarioId>`, `cargo:<cargoId>`. */
  chave: string;
  tipo: TipoNoGerado;
  rotulo: string;
  subtitulo: string;
  descricao: string;
  usuarioId: string | null;
  departamentoId: string | null;
}

export interface ArestaGerada {
  origem: string;
  destino: string;
}

export interface PessoaRef {
  usuarioId: string;
  nome: string;
}

export interface AvisosGeracao {
  totalPessoas: number;
  /** Pessoas sem departamento válido: nem na ficha nem no cargo, ou com departamento removido na ficha
   *  (vão para "Sem departamento" se também não tiverem gestor válido). */
  semDepartamento: PessoaRef[];
  /** Informativo: sem departamento na ficha (vazio), usam o departamento do cargo. */
  departamentoPeloCargo: PessoaRef[];
  semCargo: PessoaRef[];
  /** Informativo: sem gestor cadastrado (caso normal do topo de cada área). */
  semGestor: PessoaRef[];
  /** Gestor cadastrado mas inválido (fora do conjunto gerado ou a própria pessoa): voltam ao departamento. */
  gestorInvalido: PessoaRef[];
  /** Cada item é um ciclo de gestores (A responde a B e B a A); as pessoas voltam ao departamento. */
  ciclos: PessoaRef[][];
  /** Departamentos que não têm nenhuma pessoa. */
  departamentosSemPessoas: string[];
  /** Departamentos com pessoas, mas todas sob gestores de fora: não geram card. */
  departamentosSemRaiz: string[];
  /** Informativo: cargos que ninguém ocupa e sem quantidade no quadro (não aparecem no desenho). */
  cargosSemPessoas: string[];
  /** Cargos sem ninguém cadastrado que entraram pela quantidade do quadro (card de quadro). */
  cargosPeloQuadro: Array<{ nome: string; quantidade: number }>;
  /** Informativo: cargos com pessoas cadastradas abaixo do quadro informado (só as cadastradas aparecem). */
  cargosAbaixoDoQuadro: Array<{ nome: string; cadastradas: number; quadro: number }>;
}

export interface ResultadoGeracao {
  nos: NoGerado[];
  arestas: ArestaGerada[];
  avisos: AvisosGeracao;
}

export const CHAVE_EMPRESA = 'empresa';
export const CHAVE_SEM_DEPARTAMENTO = 'sem-departamento';
export const ROTULO_SEM_DEPARTAMENTO = 'Sem departamento';

const chaveDepartamento = (id: string) => `dept:${id}`;
const chavePessoa = (usuarioId: string) => `pessoa:${usuarioId}`;
const chaveCargo = (cargoId: string) => `cargo:${cargoId}`;

/** Texto do card de quadro: "10 pessoas no quadro". */
export const textoQuadro = (quantidade: number) => `${quantidade} ${quantidade === 1 ? 'pessoa' : 'pessoas'} no quadro`;

const compararNomes = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });

/**
 * Descobre quem participa de algum ciclo na cadeia de gestores.
 * Cada pessoa tem no máximo um gestor, então o grafo é funcional e cada ciclo é achado seguindo a cadeia.
 */
function encontrarCiclos(gestorDe: Map<string, string>): string[][] {
  const estado = new Map<string, 'visitando' | 'visitado'>();
  const ciclos: string[][] = [];

  for (const inicio of gestorDe.keys()) {
    if (estado.has(inicio)) continue;
    const trilha: string[] = [];
    let atual: string | undefined = inicio;

    while (atual !== undefined && !estado.has(atual)) {
      estado.set(atual, 'visitando');
      trilha.push(atual);
      atual = gestorDe.get(atual);
    }

    if (atual !== undefined && estado.get(atual) === 'visitando') {
      ciclos.push(trilha.slice(trilha.indexOf(atual)));
    }
    for (const id of trilha) estado.set(id, 'visitado');
  }

  return ciclos;
}

export function gerarOrganograma(dados: DadosGeracao): ResultadoGeracao {
  const nomeDepartamento = new Map(dados.departamentos.map((d) => [d.id, d.nome]));
  const nomeCargo = new Map(dados.cargos.map((c) => [c.id, c.nome]));
  const departamentoDoCargo = new Map(dados.cargos.map((c) => [c.id, c.departamento_id ?? null]));

  // Defesa: um usuário aparece uma vez só.
  const pessoasUnicas = new Map<string, DadosGeracaoPessoa>();
  for (const pessoa of dados.pessoas) {
    if (!pessoasUnicas.has(pessoa.usuario_id)) pessoasUnicas.set(pessoa.usuario_id, pessoa);
  }
  const pessoas = [...pessoasUnicas.values()];
  const porId = pessoasUnicas;

  const ref = (p: DadosGeracaoPessoa): PessoaRef => ({ usuarioId: p.usuario_id, nome: p.nome });
  const ordenarRefs = (lista: PessoaRef[]) => lista.sort((a, b) => compararNomes(a.nome, b.nome));

  // Departamento e cargo só valem se existirem nas listas (ativos) da empresa.
  const departamentoProprio = (p: DadosGeracaoPessoa): string | null =>
    p.departamento_id && nomeDepartamento.has(p.departamento_id) ? p.departamento_id : null;
  // Sem departamento na ficha, vale o do cargo (o cadastro de cargo já diz a que área ele pertence). Quem TEM
  // departamento na ficha, mas removido/inativo, não herda: precisa de correção e vira aviso "sem departamento".
  const departamentoPeloCargo = (p: DadosGeracaoPessoa): string | null => {
    if (p.departamento_id) return null;
    const id = p.cargo_id ? departamentoDoCargo.get(p.cargo_id) : null;
    return id && nomeDepartamento.has(id) ? id : null;
  };
  const departamentoDe = (p: DadosGeracaoPessoa): string | null => departamentoProprio(p) ?? departamentoPeloCargo(p);
  const cargoDe = (p: DadosGeracaoPessoa): string =>
    (p.cargo_id && nomeCargo.get(p.cargo_id)) || '';

  const avisos: AvisosGeracao = {
    totalPessoas: pessoas.length,
    semDepartamento: [],
    departamentoPeloCargo: [],
    semCargo: [],
    semGestor: [],
    gestorInvalido: [],
    ciclos: [],
    departamentosSemPessoas: [],
    departamentosSemRaiz: [],
    cargosSemPessoas: [],
    cargosPeloQuadro: [],
    cargosAbaixoDoQuadro: [],
  };

  // 0) Cargos sem ninguém cadastrado, com quantidade no quadro, viram um card de quadro — sob o departamento
  //    do cargo (se existir na lista) ou em "Sem departamento". Cargo com gente cadastrada mostra só as pessoas.
  const cadastradasPorCargo = new Map<string, number>();
  for (const p of pessoas) {
    if (p.cargo_id && nomeCargo.has(p.cargo_id)) {
      cadastradasPorCargo.set(p.cargo_id, (cadastradasPorCargo.get(p.cargo_id) ?? 0) + 1);
    }
  }
  const quadroPorDepartamento = new Map<string, DadosGeracaoCargo[]>();
  const quadroSemDepartamento: DadosGeracaoCargo[] = [];
  for (const cargo of dados.cargos) {
    const quadro = cargo.quantidade_quadro ?? 0;
    const cadastradas = cadastradasPorCargo.get(cargo.id) ?? 0;
    if (quadro > 0 && cadastradas === 0) {
      avisos.cargosPeloQuadro.push({ nome: cargo.nome, quantidade: quadro });
      const departamentoId = cargo.departamento_id && nomeDepartamento.has(cargo.departamento_id) ? cargo.departamento_id : null;
      if (departamentoId) {
        quadroPorDepartamento.set(departamentoId, [...(quadroPorDepartamento.get(departamentoId) ?? []), cargo]);
      } else {
        quadroSemDepartamento.push(cargo);
      }
    } else if (cadastradas > 0 && quadro > cadastradas) {
      avisos.cargosAbaixoDoQuadro.push({ nome: cargo.nome, cadastradas, quadro });
    }
  }

  // 1) Gestores candidatos (no conjunto e diferentes da própria pessoa) x inválidos.
  const gestorCandidato = new Map<string, string>();
  for (const p of pessoas) {
    if (!departamentoProprio(p)) {
      if (departamentoPeloCargo(p)) avisos.departamentoPeloCargo.push(ref(p));
      else avisos.semDepartamento.push(ref(p));
    }
    if (!cargoDe(p)) avisos.semCargo.push(ref(p));

    if (!p.gestor_usuario_id) {
      avisos.semGestor.push(ref(p));
    } else if (p.gestor_usuario_id === p.usuario_id || !porId.has(p.gestor_usuario_id)) {
      avisos.gestorInvalido.push(ref(p));
    } else {
      gestorCandidato.set(p.usuario_id, p.gestor_usuario_id);
    }
  }

  // 2) Quebra de ciclos: todas as pessoas do ciclo perdem o gestor e voltam ao departamento.
  const emCiclo = new Set<string>();
  for (const ciclo of encontrarCiclos(gestorCandidato)) {
    ciclo.forEach((id) => emCiclo.add(id));
    avisos.ciclos.push(ordenarRefs(ciclo.map((id) => ref(porId.get(id) as DadosGeracaoPessoa))));
  }
  avisos.ciclos.sort((a, b) => compararNomes(a[0].nome, b[0].nome));
  const gestorValido = new Map<string, string>();
  for (const [id, gestorId] of gestorCandidato) {
    if (!emCiclo.has(id)) gestorValido.set(id, gestorId);
  }

  // 3) Agrupa: filhos de cada gestor e raízes de cada departamento (ou "sem departamento").
  const subordinados = new Map<string, DadosGeracaoPessoa[]>();
  const raizesPorDepartamento = new Map<string, DadosGeracaoPessoa[]>();
  const raizesSemDepartamento: DadosGeracaoPessoa[] = [];

  for (const p of pessoas) {
    const gestorId = gestorValido.get(p.usuario_id);
    if (gestorId) {
      const lista = subordinados.get(gestorId) ?? [];
      lista.push(p);
      subordinados.set(gestorId, lista);
      continue;
    }
    const departamentoId = departamentoDe(p);
    if (departamentoId) {
      const lista = raizesPorDepartamento.get(departamentoId) ?? [];
      lista.push(p);
      raizesPorDepartamento.set(departamentoId, lista);
    } else {
      raizesSemDepartamento.push(p);
    }
  }

  // 4) Monta nós e arestas (pré-ordem, sempre ordenado por nome).
  const nos: NoGerado[] = [
    {
      chave: CHAVE_EMPRESA,
      tipo: 'empresa',
      rotulo: dados.empresa.nome,
      subtitulo: '',
      descricao: '',
      usuarioId: null,
      departamentoId: null,
    },
  ];
  const arestas: ArestaGerada[] = [];

  const adicionarPessoa = (pessoa: DadosGeracaoPessoa, chavePai: string, gestor: DadosGeracaoPessoa | null) => {
    const departamentoId = departamentoDe(pessoa);
    const departamentoGestor = gestor ? departamentoDe(gestor) : null;
    const mostrarDepartamento = gestor !== null && departamentoId !== departamentoGestor;
    const chave = chavePessoa(pessoa.usuario_id);

    nos.push({
      chave,
      tipo: 'pessoa',
      rotulo: pessoa.nome,
      subtitulo: cargoDe(pessoa),
      descricao: mostrarDepartamento
        ? `Depto: ${departamentoId ? nomeDepartamento.get(departamentoId) : ROTULO_SEM_DEPARTAMENTO}`
        : '',
      usuarioId: pessoa.usuario_id,
      departamentoId,
    });
    arestas.push({ origem: chavePai, destino: chave });

    const filhos = [...(subordinados.get(pessoa.usuario_id) ?? [])].sort((a, b) => compararNomes(a.nome, b.nome));
    for (const filho of filhos) adicionarPessoa(filho, chave, pessoa);
  };

  const adicionarQuadros = (cargos: DadosGeracaoCargo[], chavePai: string, departamentoId: string | null) => {
    for (const cargo of [...cargos].sort((a, b) => compararNomes(a.nome, b.nome))) {
      const chave = chaveCargo(cargo.id);
      nos.push({
        chave,
        tipo: 'cargo_quadro',
        rotulo: cargo.nome,
        subtitulo: textoQuadro(cargo.quantidade_quadro ?? 0),
        descricao: '',
        usuarioId: null,
        departamentoId,
      });
      arestas.push({ origem: chavePai, destino: chave });
    }
  };

  const departamentosOrdenados = [...dados.departamentos].sort((a, b) => compararNomes(a.nome, b.nome));
  for (const departamento of departamentosOrdenados) {
    const raizes = [...(raizesPorDepartamento.get(departamento.id) ?? [])].sort((a, b) => compararNomes(a.nome, b.nome));
    const quadros = quadroPorDepartamento.get(departamento.id) ?? [];
    if (raizes.length === 0) {
      const temPessoas = pessoas.some((p) => departamentoDe(p) === departamento.id);
      // Mesmo com card de quadro, quem é do departamento mas responde a gestor de fora fica fora dele: avisa.
      if (temPessoas) avisos.departamentosSemRaiz.push(departamento.nome);
      else if (quadros.length === 0) avisos.departamentosSemPessoas.push(departamento.nome);
      if (quadros.length === 0) continue;
    }

    const chave = chaveDepartamento(departamento.id);
    nos.push({
      chave,
      tipo: 'departamento',
      rotulo: departamento.nome,
      subtitulo: '',
      descricao: '',
      usuarioId: null,
      departamentoId: departamento.id,
    });
    arestas.push({ origem: CHAVE_EMPRESA, destino: chave });
    for (const raiz of raizes) adicionarPessoa(raiz, chave, null);
    adicionarQuadros(quadros, chave, departamento.id);
  }

  if (raizesSemDepartamento.length > 0 || quadroSemDepartamento.length > 0) {
    nos.push({
      chave: CHAVE_SEM_DEPARTAMENTO,
      tipo: 'sem_departamento',
      rotulo: ROTULO_SEM_DEPARTAMENTO,
      subtitulo: '',
      descricao: '',
      usuarioId: null,
      departamentoId: null,
    });
    arestas.push({ origem: CHAVE_EMPRESA, destino: CHAVE_SEM_DEPARTAMENTO });
    for (const raiz of [...raizesSemDepartamento].sort((a, b) => compararNomes(a.nome, b.nome))) {
      adicionarPessoa(raiz, CHAVE_SEM_DEPARTAMENTO, null);
    }
    adicionarQuadros(quadroSemDepartamento, CHAVE_SEM_DEPARTAMENTO, null);
  }

  // 5) Cargos que ninguém ocupa (os que entraram pelo quadro já aparecem no desenho).
  const cargosEmUso = new Set(pessoas.map((p) => p.cargo_id).filter((id): id is string => Boolean(id)));
  avisos.cargosSemPessoas = dados.cargos
    .filter((c) => !cargosEmUso.has(c.id) && !((c.quantidade_quadro ?? 0) > 0))
    .map((c) => c.nome)
    .sort(compararNomes);

  ordenarRefs(avisos.semDepartamento);
  ordenarRefs(avisos.departamentoPeloCargo);
  ordenarRefs(avisos.semCargo);
  ordenarRefs(avisos.semGestor);
  ordenarRefs(avisos.gestorInvalido);
  avisos.departamentosSemPessoas.sort(compararNomes);
  avisos.departamentosSemRaiz.sort(compararNomes);
  avisos.cargosPeloQuadro.sort((a, b) => compararNomes(a.nome, b.nome));
  avisos.cargosAbaixoDoQuadro.sort((a, b) => compararNomes(a.nome, b.nome));

  return { nos, arestas, avisos };
}
